<template>
  <v-container fluid>
    <v-row align="center">
      <v-col cols="10"><h3>Daily Orders</h3></v-col>
      <v-col cols="2">Total #{{ count }}</v-col>
    </v-row>

    <v-row justify="center">
      <v-btn
        fab
        color="cyan"
        absolute
        top
        class="mt-6"
        small
        right
        @click.native.stop="reload"
      >
        <v-icon color="white">refresh</v-icon>
      </v-btn>
    </v-row>
    <v-sheet elevation="1">
      <v-row v-if="buyers" justify="center" class="px-8">
        <div v-for="buyer in buyers" :key="buyer">
          <div class="pa-2 text-no-wrap">
            <v-icon :color="colors(buyer)">mdi-rectangle</v-icon> {{ buyer }}
          </div>
        </div>
      </v-row>
      <v-row>
        <v-col cols="12" md="8" class="d-flex justify-center px-8 py-4">
          <div v-resize="onResize" ref="svgContainer" class="w-100">
            <svg :width="width" :height="height" class="svgBar" />
          </div>
        </v-col>
        <v-col cols="12" md="4" v-if="selectedFeeInfo">
          <RevenueTable :tableData="selectedFeeInfo" />
        </v-col>
      </v-row>
      <v-row class="px-8">
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-dialog
            ref="dialog"
            v-model="modal"
            persistent
            :return-value.sync="select_dates"
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRangeText"
                label="Date Range"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                hide-details="auto"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="select_dates"
              range
              :max="new Date().toISOString().substr(0, 10)"
              ><v-spacer></v-spacer>
              <v-btn text color="primary" @click="modal = false">
                Cancel
              </v-btn>
              <v-btn text color="primary" @click="onSelectDate">
                OK
              </v-btn></v-date-picker
            >
          </v-dialog>
        </v-col>
        <v-col cols="12" sm="6" md="8" lg="9">
          <v-combobox
            v-model="buyers"
            :items="buyersName(orders)"
            label="By Buyers"
            multiple
            chips
            hide-details="auto"
            dense
            class="pt-1"
          ></v-combobox>
        </v-col>
      </v-row>
    </v-sheet>

    <v-btn icon to="/report" class="ml-2">
      <v-icon color="info">arrow_back</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import * as d3 from 'd3'
import resize from 'vue-resize-directive'
import RevenueTable from './RevenueTable'

export default {
  directives: {
    resize
  },
  components: {
    RevenueTable
  },
  data() {
    return {
      count: 0,
      width: 0,
      height: 400,
      modal: false,
      orders: [],
      buyers: [],
      selectedFeeInfo: null,
      select_dates: [
        // d3.timeFormat('%Y-%m-%d')(Date.now()),
        '2020-10-1',
        d3.timeFormat('%Y-%m-%d')(Date.now())
      ],
      range_dates: [
        // d3.timeFormat('%Y-%m-%d')(Date.now()),
        '2020-10-1',
        d3.timeFormat('%Y-%m-%d')(Date.now())
      ]
    }
  },
  computed: {
    ...mapGetters({
      profile: 'profile'
    }),
    dateRangeText() {
      return this.range_dates.join(' ~ ')
    },
    colors() {
      return d3
        .scaleOrdinal()
        .domain(this.buyers)
        .range(this.randomHsl(this.buyers.length))
    },
    daysInRange() {
      let date = []
      let from = this.range_dates[0],
        to = this.range_dates[1]
      while (moment(from) <= moment(to)) {
        date.push(from)
        from = moment(from)
          .add(1, 'days')
          .format('YYYY-MM-DD')
      }
      return date
    }
  },
  async mounted() {
    this.onResize()
    await this.reload()
  },
  watch: {
    range_dates() {
      this.reload()
    },
    buyers() {
      this.selectedFeeInfo = this.orders.filter(o =>
        this.buyers.includes(o.buyer.name)
      )
      this.renderChart()
    },
    width: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.renderChart()
      }
    }
  },
  methods: {
    buyersName(orderData) {
      return [...new Set(orderData.map(e => e.buyer.name).filter(e => e))]
    },
    onSelectDate() {
      this.$refs.dialog.save(this.select_dates)
      this.range_dates = this.select_dates
    },
    randomHsl(size) {
      return [...Array(size).keys()].map(
        () => `hsla(${Math.random() * 540}, 100%, 50%, 1)`
      )
    },
    onResize() {
      this.width = this.$refs.svgContainer.clientWidth
    },

    async reload() {
      await this.loadOrders()
      await this.renderChart()
    },
    async renderChart() {
      if (!this.orders) {
        console.warn(`orders is empty`)
        return
      }

      const days = this.daysInRange
      const filteredOrders = this.orders.filter(o =>
        this.buyers.includes(o.buyer.name)
      )
      let data = d3
        .nest()
        .key(e => e.deliveryDate)
        .entries(filteredOrders)

      data = data
        .map(e => {
          let record = {}
          if (e.values && e.values.length) {
            record = d3
              .nest()
              .key(d => d.buyer.name)
              .entries(e.values)
              .reduce(
                (a, c) => {
                  a[c.key] = c.values.length
                  return a
                },
                { day: e.key }
              )
          }
          return record
        })
        .sort((a, b) => new Date(a) - new Date(b))

      let maxY = 0
      const stackedData = d3.stack().keys(this.buyers)(data)

      stackedData.forEach(s => {
        let max = Math.max(...s.map(e => (isNaN(e[1]) ? 0 : e[1])))
        if (maxY < max) {
          maxY = max
        }
      })

      d3.select('.svgBar')
        .selectAll('*')
        .remove()

      const margin = { left: 30, right: 20, bottom: 20, top: 20 }
      const dateLength = 80
      const labelCounts = Math.floor(
        (this.width - margin.left - margin.right) / dateLength
      )
      const step = Math.ceil(days.length / labelCounts)
      const bars = d3
        .select('.svgBar')
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)

      const x = d3
        .scaleBand()
        .domain(days)
        .range([0, this.width - margin.right - margin.left])
        .padding([0.2])

      bars
        .append('g')
        .attr(
          'transform',
          `translate(0,${this.height - margin.top - margin.bottom})`
        )
        .call(d3.axisBottom(x).tickSizeOuter(0))
        .call(g => g.selectAll('line').remove())
        .call(g =>
          g
            .selectAll('text')
            .style('font-size', '14px')
            .text((d, i) => (i % step === 0 ? d : ''))
        )

      // Add Y axis
      const y = d3
        .scaleLinear()
        .domain([0, maxY + 1])
        .range([this.height - margin.bottom - margin.top, 0])

      bars
        .append('g')
        .call(
          d3.axisLeft(y).tickSize(-(this.width - margin.left - margin.right))
        )
        .call(g => g.selectAll('line').style('stroke', 'lightgrey'))
        .call(g =>
          g
            .selectAll('text')
            .style('font-size', '14px')
            .text(d => (Number(d) !== Number(d.toFixed(0)) ? '' : d))
        )

      bars.selectAll('.domain').remove()
      const that = this
      let hoveredData
      const bar = bars
        .append('g')
        .selectAll('g')
        .data(stackedData)
        .enter()
        .append('g')
        .attr('fill', d => this.colors(d.key))
        .on('mouseover', function(d) {
          console.log(d.key, hoveredData, filteredOrders, 'filteredOrders')
          that.selectedFeeInfo = filteredOrders.filter(
            order =>
              order.deliveryDate === hoveredData.day &&
              order.buyer.name === d.key
          )
          bar.append('title').text(`${d.key} : ${hoveredData[d.key]} order(s)`)
        })
        .on('mouseout', function() {
          bars.selectAll('title').remove()
          that.selectedFeeInfo = filteredOrders
        })

      bar
        .selectAll('rect')
        .data(e => e)
        .enter()
        .append('rect')
        .attr('x', d => x(d.data.day))
        .attr('y', d => y(d[1]))
        .attr('height', d => y(d[0] || 0) - y(d[1] || 0))
        .attr('width', x.bandwidth())
        .style('stroke', 'white')
        .style('stroke-width', 1)
        .on('mouseover', function(d) {
          hoveredData = d.data
          d3.select(this)
            .style('stroke', 'grey')
            .style('stroke-width', 3)
            .style('fill-opacity', 0.6)
            .style('cursor', 'pointer')
        })
        .on('mouseout', function() {
          d3.select(this)
            .style('stroke', 'white')
            .style('stroke-width', 1)
            .style('fill-opacity', 1)
            .style('cursor', 'default')
        })

      d3.select('.svgBar')
        .append('text')
        .attr('y', '10px')
        .style('font-size', '12px')
        .text('Total Order Counts')
    },
    async loadOrders() {
      if (this.profile && this.profile.id) {
        await this.$store
          .dispatch('loadProducerOrdersFromRange', {
            uid: this.profile.id,
            from: this.range_dates[0],
            to: this.range_dates[1],
            range: this.daysInRange
          })
          .then(orders => {
            this.count = orders.length
            this.orders = orders
            this.buyers = this.buyersName(orders)
          })
      }
    }
  }
}
</script>
